import React, { useEffect, useState, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import usePageSeo, { categorySeo } from "../hooks/usePageSeo";
import { CartContext } from "../context/CartContext";
import { AuthContext } from "../context/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import AddToCartButton from "../components/AddToCartButton";
import ProductCard from "../components/ProductCard";
import "../css/shop.css";
import "../css/SubCategory.css"; // Add breadcrumb styles
import api from "../services/api";

const SubCategory = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { addToCart } = useContext(CartContext);
  const { isAuthenticated } = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [productImages, setProductImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [subcategoryInfo, setSubcategoryInfo] = useState(null);

  usePageSeo(categorySeo(subcategoryInfo, "subcategory"));

  useEffect(() => {
    if (id) {
      setLoading(true);

      // Fetch subcategory info first
      const fetchSubcategoryInfo = async () => {
        try {
          const subcategoryRes = await api.get(`/subcategories/${id}`);
          setSubcategoryInfo(subcategoryRes.data);
        } catch (err) {
          console.error("Error fetching subcategory info:", err);
        }
      };

      // Fetch products
      const fetchProducts = async () => {
        try {
          const res = await api.get(`/products/by-subcategories?ids=${id}`);
          const productsData = res.data || [];
          setProducts(productsData);

          // The endpoint now ships each product's main image — no extra requests.
          const imagesData = {};
          productsData.forEach((product) => {
            imagesData[product._id] = product.imageUrl || "/images/default-product.jpg";
          });
          setProductImages(imagesData);
        } catch (err) {
          console.error("Error fetching products:", err);
        } finally {
          setLoading(false);
        }
      };

      Promise.all([fetchSubcategoryInfo(), fetchProducts()]);
    }
  }, [id]);

  const handleAddToCart = async (product) => {
    if (!isAuthenticated) {
      toast.error(t("auth.loginRequired"), {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }

    try {
      await api.post("/cart", {
        product_id: product._id,
        quantity: 1,
      });

      addToCart({
        ...product,
        newPrice: product.is_on_sale ? product.sale_price : product.sell_price,
        quantity: 1,
      });

      toast.success(t("product.addedToCart"), {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error adding product to cart:", error);
      toast.error(t("product.addToCartFailed"), {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  if (loading)
    return <div className="text-center mt-5">{t("shop.loading")}</div>;

  return (
    <div className="container-fluid">
      <ToastContainer />

      {/* Breadcrumb Navigation */}
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-container breadcrumb-gray-theme">
        <nav aria-label="breadcrumb" className="breadcrumb-nav">
          <ol className="breadcrumb">
            {/* Home Link */}
            <li className="breadcrumb-item">
              <Link to="/" className="breadcrumb-link">
                {t("nav.home")}
              </Link>
              <span className="breadcrumb-separator"></span>
            </li>

            {/* Category Link */}
            <li className="breadcrumb-item">
              <Link
                to={`/categories/${subcategoryInfo?.category_id?._id}/products`}
                className="breadcrumb-link"
              >
                {subcategoryInfo?.category_id?.name}
              </Link>
              <span className="breadcrumb-separator"></span>
            </li>

            {/* Subcategory (Active) */}
            <li className="breadcrumb-item active" aria-current="page">
              <span className="breadcrumb-subcategory">
                {subcategoryInfo?.name}
              </span>
            </li>
          </ol>
        </nav>
      </div>

      <div className="container py-4">
        {/* <div className="mb-4">
          <Link
            to="/shop"
            className="btn btn-outline-warning d-inline-flex align-items-center"
          >
            <span className="d-sm-none fs-4">←</span>
            <span className="d-none d-sm-inline">← Back to Shop</span>
          </Link>
        </div> */}

        {products.length === 0 ? (
          <div className="text-center mt-5">{t("shop.noProducts")}</div>
        ) : (
          <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-4">
            {products.map((product) => {
              const price = product.is_on_sale
                ? product.sale_price
                : product.sell_price;
              const imageUrl =
                productImages[product._id] || "/images/default-product.jpg";

              return (
                <div className="col" key={product._id}>
                  <ProductCard
                    product={{ ...product, newPrice: price }}
                    productImageUrl={imageUrl}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubCategory;
